import { always, compose, cond, either, isEmpty, isNil, not, pickBy, test } from 'ramda';
import { isValidNumberForRegion } from 'libphonenumber-js';

export const REQUIRED_ERROR = 'required_label';
export const VALIDATE_ERROR = 'invalid';
export const WRONG_FORMAT = 'wrongFormat';
export const MUST_BE_NUMBER = 'must_be_number';
export const MUST_BE_POSITIVE = 'must_be_positive';
export const MUST_BE_INTEGER = 'must_be_integer';
export const MUST_BE_BETWEEN = 'must_be_between';
export const MUST_BE_AT_LEAST = 'must_be_at_least';
export const CANNOT_BE_LESS_THAN_LICENSE_YEAR = 'cannot_be_less_than_license_year';

export const DEFAULT_MINIMUM_AGE = 21;
const DEFAULT_MAXIMUM_AGE = 99;
export const DEFAULT_MINIMUM_DRIVING_LICENSE_VALIDATION = 2;
const CURRENT_YEAR = new Date().getFullYear();
const OLDEST_ACCEPTED_BIRTH_YEAR = CURRENT_YEAR - DEFAULT_MAXIMUM_AGE;

export const isValidPhoneNumber = (value = '', country) => isValidNumberForRegion(value, country.toUpperCase());

const isPhoneNumber = country => phone => {
  if (isValidPhoneNumber(phone, country) && !test(/[^0-9]+$/, phone) && !test(/^[^0-9+]+/, phone)) return false;
  return true;
};

const isValidEmail = email =>
  email && /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email) === false;
const isValidVin = vin => vin.length === 17 && /^[A-Z0-9]+$/.test(vin);
const isNegativeNumber = number => number < 0;

export const validateFirstname = cond([[isEmpty, always(REQUIRED_ERROR)]]);
export const validateLastname = cond([[isEmpty, always(REQUIRED_ERROR)]]);
export const validateGarageName = cond([[either(isEmpty, isNil), always(REQUIRED_ERROR)]]);
export const validateAddress = cond([[either(isEmpty, isNil), always(REQUIRED_ERROR)]]);
export const validateCarPlate = cond([[isEmpty, always(REQUIRED_ERROR)]]);
export const validateEmail = cond([[isEmpty, always(REQUIRED_ERROR)], [isValidEmail, always(WRONG_FORMAT)]]);
export const validateEmailNotRequired = cond([[isValidEmail, always(WRONG_FORMAT)]]);
const validateMileage = cond([[isEmpty, always(REQUIRED_ERROR)], [isNegativeNumber, always(MUST_BE_POSITIVE)]]);

export const validatePhoneNumber = country =>
  cond([[isEmpty, always(REQUIRED_ERROR)], [isPhoneNumber(country), always(VALIDATE_ERROR)]]);

export const validatePhoneNumberNotRequired = country =>
  cond([[isEmpty, always(undefined)], [isPhoneNumber(country), always(VALIDATE_ERROR)]]);

export const validateCarVin = (vin, isVinMandatory = false) => {
  if (isVinMandatory === true) {
    if (isEmpty(vin)) return REQUIRED_ERROR;
  }

  if (vin.length !== 0 && !isValidVin(vin)) return WRONG_FORMAT;
};

const isNotNumber = value => {
  const num = Number(value);
  return typeof num === 'number' && Number.isNaN(num);
};

const isNotInteger = value => {
  const num = Number(value);
  return !Number.isInteger(num);
};

const isNotValidAge = (number, minAge) => number < (minAge || DEFAULT_MINIMUM_AGE) || number > DEFAULT_MAXIMUM_AGE;

export const validateAge = (value, settings) => {
  const minAge = settings?.RENTAL_WORKFLOW?.DRIVER_MINIMUM_AGE || DEFAULT_MINIMUM_AGE;

  return cond([
    [() => isEmpty(value), always(REQUIRED_ERROR)],
    [() => isNotNumber(value), always(MUST_BE_NUMBER)],
    [() => isNegativeNumber(value), always(MUST_BE_POSITIVE)],
    [() => isNotInteger(value), always(MUST_BE_INTEGER)],
    [() => isNotValidAge(value, minAge), always(MUST_BE_BETWEEN)],
  ])(value);
};

const isNotValidDrivingLicenseYear = (number, yearLimit) => {
  const drivingLicenseAge = CURRENT_YEAR - number;

  if (CURRENT_YEAR < number || number < OLDEST_ACCEPTED_BIRTH_YEAR) {
    return true;
  }

  return drivingLicenseAge <= (yearLimit || DEFAULT_MINIMUM_DRIVING_LICENSE_VALIDATION);
};

const isNotValidDrivingLicenseYearConsideringAge = (licenseYear, minAge, age) => {
  const yearsOfBirth = CURRENT_YEAR - age;
  const ageWhenGetLicence = licenseYear - yearsOfBirth;

  return ageWhenGetLicence < minAge;
};

export const validateDrivingLicenseYear = (value, settings, age) => {
  const yearLimit =
    settings?.RENTAL_WORKFLOW?.DRIVER_MINIMUM_DRIVING_LICENSE_YEAR || DEFAULT_MINIMUM_DRIVING_LICENSE_VALIDATION;
  const minAge = settings?.RENTAL_WORKFLOW?.DRIVER_MINIMUM_AGE || DEFAULT_MINIMUM_AGE;

  return cond([
    [() => isEmpty(value), always(REQUIRED_ERROR)],
    [() => isNotNumber(value), always(MUST_BE_NUMBER)],
    [() => isNegativeNumber(value), always(MUST_BE_POSITIVE)],
    [() => isNotInteger(value), always(MUST_BE_INTEGER)],
    [() => isNotValidDrivingLicenseYear(value, yearLimit), always(MUST_BE_AT_LEAST)],
    [() => isNotValidDrivingLicenseYearConsideringAge(value, minAge, age), always(CANNOT_BE_LESS_THAN_LICENSE_YEAR)],
  ])(value, age);
};

export default (
  values,
  {
    ELIGIBILITYCHECK_FIRSTNAME_DISPLAYED,
    ELIGIBILITYCHECK_LASTNAME_DISPLAYED,
    ELIGIBILITYCHECK_PHONE_NUMBER_DISPLAYED,
    ELIGIBILITYCHECK_CAR_PLATE_DISPLAYED,
    ELIGIBILITYCHECK_MILEAGE_DISPLAYED,
    ELIGIBILITYCHECK_CAR_VIN_DISPLAYED,
    VIN_MANDATORY,
  },
) => {
  const errors = {};
  /* eslint-disable */
  ELIGIBILITYCHECK_FIRSTNAME_DISPLAYED && (errors.firstName = validateFirstname(values.firstName));
  ELIGIBILITYCHECK_LASTNAME_DISPLAYED && (errors.lastName = validateLastname(values.lastName));
  ELIGIBILITYCHECK_PHONE_NUMBER_DISPLAYED &&
    (errors.phoneNumber = validatePhoneNumber(values.phoneCountry)(values.phoneNumber));
  ELIGIBILITYCHECK_CAR_PLATE_DISPLAYED &&
    (errors.plateNumber = validateCarPlate(values.plateNumber, VIN_MANDATORY, ELIGIBILITYCHECK_CAR_VIN_DISPLAYED));
  ELIGIBILITYCHECK_MILEAGE_DISPLAYED && (errors.mileage = validateMileage(values.mileage));
  ELIGIBILITYCHECK_CAR_VIN_DISPLAYED && (errors.vinNumber = validateCarVin(values.vinNumber, VIN_MANDATORY));
  /* eslint-enable */
  return pickBy(
    compose(
      not,
      isNil,
    ),
    errors,
  );
};
